/**
 * Utilitários para formatação de dados
 */

/**
 * Formatar valor monetário em Real (BRL)
 */
export const formatCurrency = (value: number | string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return 'R$ 0,00';
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/**
 * Converte um valor de data vindo da API em Date, sem deslocar o dia.
 *
 * O backend serializa colunas `date` como meia-noite UTC ("2026-08-17T00:00:00Z").
 * `new Date()` sobre essa string produz 21:00 do dia ANTERIOR no horário de Brasília,
 * e a data exibida retrocedia um dia. Para data pura o correto é montar a data no
 * fuso local a partir dos componentes ano/mês/dia — data de vencimento não tem fuso.
 */
export const parseApiDate = (date: string | Date): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date;

  const soData = date.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00:00(?:\.0+)?Z?)?$/);
  if (soData) {
    const [, ano, mes, dia] = soData;
    return new Date(Number(ano), Number(mes) - 1, Number(dia));
  }

  const dateObj = new Date(date);
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

/**
 * Formatar data no padrão brasileiro
 */
export const formatDate = (date: string | Date): string => {
  const dateObj = parseApiDate(date);
  if (!dateObj) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
};

/**
 * Formatar data e hora no padrão brasileiro
 */
export const formatDateTime = (date: string | Date): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
};

/**
 * Formatar número com separadores de milhares
 */
export const formatNumber = (value: number | string): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return '0';
  }

  return new Intl.NumberFormat('pt-BR').format(numValue);
};

/**
 * Formatar CPF
 */
export const formatCPF = (cpf: string): string => {
  if (!cpf) return '';
  
  // Remove caracteres não numéricos
  const numbers = cpf.replace(/\D/g, '');
  
  // Aplica a máscara
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Formatar CNPJ
 */
export const formatCNPJ = (cnpj: string): string => {
  if (!cnpj) return '';
  
  // Remove caracteres não numéricos
  const numbers = cnpj.replace(/\D/g, '');
  
  // Aplica a máscara
  return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

/**
 * Formatar telefone
 */
export const formatPhone = (phone: string): string => {
  if (!phone) return '';
  
  // Remove caracteres não numéricos
  const numbers = phone.replace(/\D/g, '');
  
  // Aplica a máscara baseada no tamanho
  if (numbers.length === 11) {
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  } else if (numbers.length === 10) {
    return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  
  return phone;
};

/**
 * Formatar CEP
 */
export const formatCEP = (cep: string): string => {
  if (!cep) return '';
  
  // Remove caracteres não numéricos
  const numbers = cep.replace(/\D/g, '');
  
  // Aplica a máscara
  return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
};

/**
 * Formatar quilometragem
 */
export const formatKilometers = (km: number | string): string => {
  const numValue = typeof km === 'string' ? parseFloat(km) : km;
  
  if (isNaN(numValue)) {
    return '0 km';
  }

  return `${formatNumber(numValue)} km`;
};

/**
 * Formatar litros
 */
export const formatLiters = (liters: number | string): string => {
  const numValue = typeof liters === 'string' ? parseFloat(liters) : liters;
  
  if (isNaN(numValue)) {
    return '0 L';
  }

  return `${formatNumber(numValue)} L`;
};

/**
 * Formatar consumo (km/L)
 */
export const formatConsumption = (consumption: number | string): string => {
  const numValue = typeof consumption === 'string' ? parseFloat(consumption) : consumption;
  
  if (isNaN(numValue)) {
    return '0 km/L';
  }

  return `${formatNumber(numValue)} km/L`;
};

/**
 * Formatar status de veículo
 */
export const formatVehicleStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    ativo: 'Ativo',
    inativo: 'Inativo',
    manutencao: 'Em Manutenção',
    vendido: 'Vendido'
  };
  
  return statusMap[status] || status;
};

/**
 * Formatar status de motorista
 */
export const formatDriverStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    ativo: 'Ativo',
    inativo: 'Inativo',
    suspenso: 'Suspenso',
    licenciado: 'Licenciado'
  };
  
  return statusMap[status] || status;
};

/**
 * Formatar categoria de veículo
 */
export const formatVehicleCategory = (category: string): string => {
  const categoryMap: Record<string, string> = {
    passeio: 'Passeio',
    utilitario: 'Utilitário',
    comercial: 'Comercial',
    motocicleta: 'Motocicleta',
    caminhao: 'Caminhão'
  };
  
  return categoryMap[category] || category;
};

/**
 * Formatar categoria de despesa
 */
export const formatExpenseCategory = (category: string): string => {
  const categoryMap: Record<string, string> = {
    combustivel: 'Combustível',
    manutencao: 'Manutenção',
    seguro: 'Seguro',
    ipva: 'IPVA',
    licenciamento: 'Licenciamento',
    multa: 'Multa',
    pedagio: 'Pedágio',
    estacionamento: 'Estacionamento',
    lavagem: 'Lavagem',
    financiamento: 'Financiamento',
    depreciacao: 'Depreciação',
    outros: 'Outros'
  };
  
  return categoryMap[category] || category;
};

/**
 * Formatar tipo de despesa
 */
export const formatExpenseType = (type: string): string => {
  const typeMap: Record<string, string> = {
    fixa: 'Fixa',
    variavel: 'Variável',
    eventual: 'Eventual'
  };
  
  return typeMap[type] || type;
};

/**
 * Formatar status de despesa
 */
export const formatExpenseStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    paga: 'Paga',
    pendente: 'Pendente',
    vencida: 'Vencida',
    cancelada: 'Cancelada'
  };
  
  return statusMap[status] || status;
};

/**
 * Formatar forma de pagamento
 */
export const formatPaymentMethod = (method: string): string => {
  const methodMap: Record<string, string> = {
    dinheiro: 'Dinheiro',
    debito: 'Débito',
    credito: 'Crédito',
    pix: 'PIX',
    boleto: 'Boleto',
    transferencia: 'Transferência',
    cheque: 'Cheque'
  };
  
  return methodMap[method] || method;
};

/**
 * Formatar tempo relativo (ex: "há 2 dias")
 */
export const formatRelativeTime = (date: string | Date): string => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);
  
  if (diffInSeconds < 60) {
    return 'agora mesmo';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `há ${minutes} minuto${minutes > 1 ? 's' : ''}`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `há ${hours} hora${hours > 1 ? 's' : ''}`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `há ${days} dia${days > 1 ? 's' : ''}`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `há ${months} mês${months > 1 ? 'es' : ''}`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `há ${years} ano${years > 1 ? 's' : ''}`;
  }
};

/**
 * Formatar texto com primeira letra maiúscula
 */
export const capitalize = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Formatar texto com todas as palavras capitalizadas
 */
export const capitalizeWords = (text: string): string => {
  if (!text) return '';
  return text.split(' ').map(word => capitalize(word)).join(' ');
};
